import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Gavel as GavelIcon,
  MonetizationOn as MonetizationOnIcon,
  Search as SearchIcon,
} from "@material-ui/icons";

import { LoadingButton } from "@material-ui/lab";

import { Fragment, useState } from "react";

import {
  useObtenerSugerenciasQuery,
  useBulkConciliarMutation,
} from "src/services/conciliacionApi";

import {
  formatearMonto,
  formatearRutDisplay,
  getScoreConfig,
  getBancoConfig,
  calcularSumaFacturas,
} from "src/utils/conciliacionHelpers";

import { useSnackbar } from "src/components/snackbar";

import { makeStyles } from "@material-ui/styles";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from "@material-ui/core";
import {
  obtenerConfigBadgeRegla,
  tieneReglasAplicadas,
  requiereGestionAbono,
} from "./utils";
import { formatearFechaDisplay } from "src/utils/formatTime";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  scoreBadge: {
    fontWeight: "bold",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
  },
  mejorMatchCard: {
    backgroundColor: theme.palette.success.light,
    border: `2px solid ${theme.palette.success.main}`,
    marginBottom: theme.spacing(2),
  },
  candidatoRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  razonesChip: {
    margin: theme.spacing(0.5),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
}));

/**
 * Modal para mostrar sugerencias de conciliación automática
 *
 * @param {boolean} open - Controla si el modal está abierto
 * @param {function} onClose - Callback al cerrar el modal
 * @param {Object} movimiento - Objeto del movimiento bancario seleccionado
 * @param {function} onConciliarManual - Callback para abrir el modal de conciliación manual
 * @param {function} onSuccess - Callback para completar la conciliación
 */
export default function SugerenciasModal({
  open,
  onClose,
  movimiento,
  onConciliarManual,
  onSuccess,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Estado para manejar expansión de combinaciones
  const [expandedCombos, setExpandedCombos] = useState(new Set());
  // Estado para manejar expansión de razones de candidatos
  const [expandedCandidatos, setExpandedCandidatos] = useState(new Set());

  // Estado para procesar conciliación automática
  const [procesando, setProcesando] = useState(false);

  // Query para obtener sugerencias (se ejecuta automáticamente cuando movimiento.id existe)
  const {
    data: sugerencias,
    isLoading,
    isFetching,
    isError,
    error,
  } = useObtenerSugerenciasQuery(movimiento?.id, {
    skip: !movimiento?.id || !open, // Solo ejecutar si hay movimiento y modal está abierto
  });

  // Mutation para conciliación automática (bulk)
  const [bulkConciliar, { isLoading: isConciliando }] =
    useBulkConciliarMutation();

  // Valida si el mejor match tiene contenido real
  const tieneMejorMatch = () => {
    return (
      sugerencias?.mejor_match &&
      Object.keys(sugerencias.mejor_match).length > 0 &&
      sugerencias.mejor_match.facturas &&
      sugerencias.mejor_match.facturas.length > 0
    );
  };

  // Valida si hay candidatos individuales
  const tieneCandidatos = () => {
    return (
      sugerencias?.candidatos_individuales &&
      sugerencias.candidatos_individuales.length > 0
    );
  };

  // Valida si hay combinaciones posibles
  const tieneCombinaciones = () => {
    return (
      sugerencias?.combinaciones_posibles &&
      sugerencias.combinaciones_posibles.length > 0
    );
  };

  // Toggle para expandir/colapsar combinaciones
  const handleToggleCombo = (index) => {
    setExpandedCombos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Toggle para expandir/colapsar razones de candidatos
  const handleToggleCandidato = (index) => {
    setExpandedCandidatos((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Cerrar modal y limpiar estados
  const handleClose = () => {
    if (procesando || isConciliando) return; // No cerrar si está procesando
    setExpandedCombos(new Set());
    setExpandedCandidatos(new Set());
    onClose();
  };

  // Conciliar automáticamente el mejor match
  const handleConciliarAutomatica = async () => {
    if (!sugerencias?.mejor_match) return;

    setProcesando(true);
    try {
      // Preparar operaciones para bulk conciliar
      const operaciones = [
        {
          movimiento_id: sugerencias.movimiento.id,
          facturas: sugerencias.mejor_match.facturas.map((f) => ({
            tipo: f.tipo,
            id: f.id,
            monto_asignado: f.monto,
          })),
          observaciones: `Conciliacion desde Modal Sugerencias con nivel: ${sugerencias.mejor_match.nivel} (Score: ${sugerencias.mejor_match.score})`,
        },
      ];

      const resultado = await bulkConciliar({
        operaciones,
        observaciones_generales:
          "Conciliación ejecutada desde modal de sugerencias",
      }).unwrap();

      const exitosos = resultado.resultados?.exitosos?.length || 0;
      const fallidos = resultado.resultados?.fallidos?.length || 0;

      if (exitosos > 0) {
        enqueueSnackbar(
          `${exitosos} movimiento${exitosos > 1 ? "s" : ""} conciliado${
            exitosos > 1 ? "s" : ""
          } exitosamente`,
          { variant: "success" },
        );

        // NOTIFICAR AL PADRE QUE SE CONCILIÓ EXITOSAMENTE
        if (onSuccess && typeof onSuccess === "function") {
          // Construir el detalle del movimiento conciliado para agregar a recienConciliados
          const detalleConciliado = {
            movimiento_id: sugerencias.movimiento.id,
            status: "SUGERIDO",
            score: sugerencias.mejor_match.score,
            nivel: sugerencias.mejor_match.nivel,
            movimiento: {
              ...sugerencias.movimiento,
              fecha_mov: sugerencias.movimiento.fecha,
            },
            facturas: sugerencias.mejor_match.facturas,
            conciliadoEn: new Date().toISOString(),
          };

          onSuccess(detalleConciliado);
        }
      }

      if (fallidos > 0) {
        enqueueSnackbar(
          `${fallidos} movimiento${fallidos > 1 ? "s" : ""} no pudo${
            fallidos > 1 ? "ron" : ""
          } ser conciliado${fallidos > 1 ? "s" : ""}`,
          { variant: "warning" },
        );
      }

      onClose();
    } catch (error) {
      console.error("Error al conciliar:", error);
      enqueueSnackbar(
        error?.data?.message || "Error al realizar la conciliación",
        { variant: "error" },
      );
    } finally {
      setProcesando(false);
    }
  };

  // Renderiza la información del movimiento
  const renderInfoMovimiento = () => {
    if (!movimiento) return null;

    const bancoConfig = getBancoConfig(movimiento.banco);

    return (
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          MOVIMIENTO BANCARIO
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
              <Box mb={1}>
                <Typography variant="caption" color="textSecondary">
                  Fecha
                </Typography>
                <Typography variant="body2">
                  {formatearFechaDisplay(movimiento.fecha_mov)}
                </Typography>
              </Box>

              <Box mb={1}>
                <Typography variant="caption" color="textSecondary">
                  Banco
                </Typography>
                <Box>
                  <Chip
                    label={bancoConfig.label}
                    size="small"
                    style={{
                      backgroundColor: bancoConfig.bgColor,
                      color: bancoConfig.color,
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Box>

              <Box mb={1}>
                <Typography variant="caption" color="textSecondary">
                  Monto
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 700,
                    color:
                      movimiento.tipo === "INGRESO" ? "#4CAF50" : "#F44336",
                  }}
                >
                  {formatearMonto(movimiento.monto)}
                </Typography>
              </Box>

              <Box mb={1}>
                <Typography variant="caption" color="textSecondary">
                  RUT Ordenante
                </Typography>
                <Typography variant="body2">
                  {formatearRutDisplay(movimiento.rut_ordenante)}
                </Typography>
              </Box>
            </Box>

            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Nombre Ordenante
              </Typography>
              <Typography variant="body2">
                {movimiento.nombre_ordenante || "Sin nombre"}
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Glosa
              </Typography>
              <Typography variant="body2" style={{ fontSize: "0.85rem" }}>
                {movimiento.glosa || "Sin glosa"}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Renderiza el mejor match si existe
  const renderMejorMatch = () => {
    // Validar que realmente haya un mejor match con datos
    if (!tieneMejorMatch()) return null;

    const mejorMatch = sugerencias.mejor_match;
    const scoreConfig = getScoreConfig(mejorMatch.score);

    return (
      <Box className={classes.section}>
        <Box display="flex" alignItems="center" mb={1}>
          <CheckCircleIcon style={{ color: "#4CAF50", marginRight: 8 }} />
          <Typography variant="subtitle2">MEJOR COINCIDENCIA</Typography>
          <Box ml="auto">
            <Chip
              label={scoreConfig.label}
              size="small"
              style={{
                backgroundColor: scoreConfig.badgeColor,
                color: "#FFF",
                fontWeight: 700,
              }}
            />
            <Chip
              label={`Score: ${mejorMatch.score}`}
              size="small"
              style={{
                marginLeft: 8,
                backgroundColor: scoreConfig.badgeColor,
                color: "#FFF",
                fontWeight: 700,
              }}
            />
          </Box>
        </Box>

        <Card className={classes.mejorMatchCard}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Tipo
                </Typography>
                <Typography variant="body2">
                  {mejorMatch.tipo === "INDIVIDUAL"
                    ? "Conciliación 1:1"
                    : "Conciliación 1:N (Múltiples facturas)"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Nivel de Confianza
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {scoreConfig.description}
                </Typography>
              </Box>
            </Box>

            {/* Facturas del mejor match */}
            <Box mt={2}>
              <Typography variant="caption" color="textSecondary">
                Facturas a conciliar
              </Typography>
              <Box mt={1}>
                {mejorMatch?.facturas.map((factura, index) => (
                  <Box
                    key={index}
                    mb={1}
                    p={1.5}
                    style={{
                      backgroundColor: "#FFF",
                      borderRadius: 4,
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    {/* Fila principal: Tipo/Folio + Monto */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box flex={1}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.5}
                        >
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600 }}
                          >
                            {factura.tipo} #{factura.folio}
                          </Typography>

                          {/* Badge de estado ABONO */}
                          {factura.estado === "ABONO" && (
                            <Chip
                              label="ABONO"
                              size="small"
                              icon={<MonetizationOnIcon />}
                              style={{
                                backgroundColor: "#FF9800",
                                color: "#FFF",
                                fontSize: "0.7rem",
                                height: 20,
                              }}
                            />
                          )}
                        </Box>

                        <Typography variant="caption" color="textSecondary">
                          {factura.nombre_cliente} (
                          {formatearRutDisplay(factura.rut_cliente)})
                        </Typography>
                      </Box>

                      {/* Columna de montos */}
                      <Box textAlign="right">
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {formatearMonto(factura.monto)}
                        </Typography>

                        {/* Mostrar monto original si tiene pagos */}
                        {factura.tiene_pagos && factura.factura?.valorPesos && (
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            display="block"
                            style={{ fontSize: "0.7rem" }}
                          >
                            Original:{" "}
                            {formatearMonto(
                              factura.factura.valorPesos ||
                                factura.factura.total,
                            )}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Información adicional si tiene pagos previos */}
                    {factura.tiene_pagos && factura.deuda_restante > 0 && (
                      <Box
                        mt={1}
                        pt={1}
                        style={{
                          borderTop: "1px solid #E0E0E0",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ fontSize: "0.7rem" }}
                        >
                          💰 Esta factura tiene pagos previos. El monto mostrado
                          es la deuda restante.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Razones del match */}
            {mejorMatch.razones && mejorMatch.razones.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  Razones de la coincidencia
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap">
                  {mejorMatch.razones.map((razon, index) => (
                    <Chip
                      key={index}
                      label={razon}
                      size="small"
                      className={classes.razonesChip}
                      style={{
                        backgroundColor: "#E8F5E9",
                        color: "#2E7D32",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/*  BADGES DE REGLAS CRÍTICAS */}
            {tieneReglasAplicadas(mejorMatch) && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  Información adicional
                </Typography>
                {renderBadgesReglas()}
              </Box>
            )}

            {/* Diferencia de monto si aplica */}
            {mejorMatch.diferencia !== undefined &&
              mejorMatch.diferencia !== 0 && (
                <Box mt={2}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    Diferencia de monto: {formatearMonto(mejorMatch.diferencia)}
                  </Alert>
                </Box>
              )}

            {/* SECCIÓN DE ACCIONES */}
            <Box mt={3}>
              {/* Alert diferenciado para EXCESO o FALTANTE */}
              {mejorMatch &&
                mejorMatch.tipo_diferencia &&
                (() => {
                  const esExceso = mejorMatch.tipo_diferencia === "EXCESO";
                  const esFaltante = mejorMatch.tipo_diferencia === "FALTANTE";
                  const montoDiferencia = mejorMatch.monto_diferencia || 0;
                  const sumaFacturas =
                    mejorMatch.suma_facturas ||
                    calcularSumaFacturas(mejorMatch.facturas);
                  const montoMovimiento =
                    mejorMatch.monto_movimiento || movimiento?.monto || 0;

                  return (
                    <Alert
                      severity={esExceso ? "warning" : "info"}
                      icon={<MonetizationOnIcon />}
                      style={{ marginBottom: 16 }}
                    >
                      <Typography variant="body2" gutterBottom>
                        <strong>
                          {esExceso && "💰 Se detectó un exceso en el pago"}
                          {esFaltante &&
                            "⚠️ Pago insuficiente: No cubre todas las facturas"}
                        </strong>
                      </Typography>
                      <Box mt={1} mb={1}>
                        <Typography variant="caption" component="div">
                          • Suma de facturas:{" "}
                          <strong>{formatearMonto(sumaFacturas)}</strong>
                        </Typography>
                        <Typography variant="caption" component="div">
                          • Monto del movimiento:{" "}
                          <strong>{formatearMonto(montoMovimiento)}</strong>
                        </Typography>
                        <Typography
                          variant="caption"
                          component="div"
                          style={{ color: esExceso ? "#f57c00" : "#1976d2" }}
                        >
                          • {esExceso ? "Exceso" : "Faltante"}:{" "}
                          <strong>{formatearMonto(montoDiferencia)}</strong>
                        </Typography>
                      </Box>
                      <Typography variant="caption">
                        {esExceso && (
                          <>
                            Puede conciliar las facturas ahora y gestionar el
                            abono después, o buscar una factura específica para
                            aplicar el exceso inmediatamente.
                          </>
                        )}
                        {esFaltante && (
                          <>
                            <strong>Distribución automática:</strong> Se pagarán
                            las facturas más antiguas (por folio) primero. La
                            última factura quedará con estado{" "}
                            <strong>ABONO</strong> por el monto disponible
                            restante.
                          </>
                        )}
                      </Typography>
                    </Alert>
                  );
                })()}

              {/* Alert de Validación Humana - Solo si NO hay gestión de abono */}
              {!requiereGestionAbono(mejorMatch) &&
                mejorMatch.requiere_validacion_humana && (
                  <Alert
                    severity="warning"
                    icon={<WarningIcon />}
                    style={{ marginBottom: 16 }}
                  >
                    <Typography variant="caption">
                      Esta sugerencia requiere validación manual debido a RUT
                      relacionado
                    </Typography>
                  </Alert>
                )}

              {/* Botones de Acción */}
              <Box
                display="flex"
                gap={2}
                justifyContent="flex-end"
                alignItems="center"
              >
                {requiereGestionAbono(mejorMatch) ? (
                  <>
                    {/* Botón Secundario: Buscar Factura para Abono */}
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<SearchIcon />}
                      onClick={() => {
                        handleClose();
                        if (onConciliarManual) {
                          onConciliarManual(movimiento);
                        }
                      }}
                      disabled={procesando || isConciliando}
                    >
                      Buscar Factura para Abono Ahora
                    </Button>

                    {/* Botón Principal: Conciliar y Gestionar Abono Después */}
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleConciliarAutomatica}
                      loading={procesando || isConciliando}
                    >
                      Conciliar Facturas (Gestionar Abono Después)
                    </LoadingButton>
                  </>
                ) : (
                  /* Botón Normal cuando NO hay gestión de abono */
                  <LoadingButton
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleConciliarAutomatica}
                    loading={procesando || isConciliando}
                  >
                    {mejorMatch.requiere_validacion_humana
                      ? "Validar y Conciliar"
                      : "Aceptar y Conciliar"}
                  </LoadingButton>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  // Renderiza badges informativos cuando se aplican reglas críticas
  const renderBadgesReglas = () => {
    if (!tieneMejorMatch() || !tieneReglasAplicadas()) return null;

    const mejorMatch = sugerencias.mejor_match;

    return (
      <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
        {/* Badge: Regla Aplicada */}
        {mejorMatch.aplicada_por_regla && (
          <Tooltip
            title={
              obtenerConfigBadgeRegla(mejorMatch.aplicada_por_regla).tooltip
            }
            arrow
          >
            <Chip
              icon={<GavelIcon />}
              label={
                obtenerConfigBadgeRegla(mejorMatch.aplicada_por_regla).label
              }
              size="small"
              style={{
                backgroundColor: obtenerConfigBadgeRegla(
                  mejorMatch.aplicada_por_regla,
                ).bgColor,
                color: "#FFF",
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}

        {/* Badge: Requiere Validación */}
        {mejorMatch.requiere_validacion_humana && (
          <Tooltip
            title="Este match requiere revisión y validación manual antes de conciliar automáticamente"
            arrow
          >
            <Chip
              icon={<InfoIcon />}
              label="Requiere Validación"
              size="small"
              style={{
                backgroundColor: "#FF9800",
                color: "#FFF",
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}

        {/* Badge: Priorizado por Monto Exacto */}
        {mejorMatch.priorizado_por_monto_exacto && (
          <Tooltip
            title="Match priorizado por tener coincidencia exacta de monto"
            arrow
          >
            <Chip
              icon={<MonetizationOnIcon />}
              label="Monto Exacto"
              size="small"
              style={{
                backgroundColor: "#4CAF50",
                color: "#FFF",
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}

        {/* Mostrar score original si fue modificado por regla */}
        {mejorMatch.score_original &&
          mejorMatch.score !== mejorMatch.score_original && (
            <Tooltip
              title={`Score original: ${mejorMatch.score_original} (ajustado por regla a ${mejorMatch.score})`}
              arrow
            >
              <Chip
                label={`Score original: ${mejorMatch.score_original}`}
                size="small"
                variant="outlined"
                style={{
                  borderColor: "#9E9E9E",
                  color: "#616161",
                }}
              />
            </Tooltip>
          )}
      </Box>
    );
  };

  // Renderiza la tabla de candidatos individuales
  const renderCandidatosIndividuales = () => {
    if (!tieneCandidatos()) return null;

    const candidatos = sugerencias.candidatos_individuales;

    return (
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          CANDIDATOS INDIVIDUALES ({candidatos.length})
        </Typography>
        <Typography variant="caption" color="textSecondary" paragraph>
          Facturas que podrían corresponder a este movimiento
        </Typography>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 50 }}></TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Folio</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell align="center">Score</TableCell>
                <TableCell>Nivel</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidatos.map((candidato, index) => {
                const isExpanded = expandedCandidatos.has(index);
                const scoreConfig = getScoreConfig(candidato.score);
                const key = candidato.folio;

                return (
                  <Fragment key={key}>
                    {/* Fila principal */}
                    <TableRow
                      key={`princ-${key}`}
                      hover
                      className={classes.candidatoRow}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleCandidato(index)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={candidato.tipo}
                          size="small"
                          style={{
                            backgroundColor:
                              candidato.tipo === "PLAN" ? "#E3F2FD" : "#FFF3E0",
                            color:
                              candidato.tipo === "PLAN" ? "#1976D2" : "#F57C00",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          #{candidato.folio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          style={{ maxWidth: 200 }}
                        >
                          {candidato.nombre_cliente || "Sin nombre"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatearRutDisplay(candidato.rut_cliente)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {candidato.estado === "ABONO" ? (
                          <Chip
                            label="ABONO"
                            size="small"
                            icon={<MonetizationOnIcon />}
                            style={{
                              backgroundColor: "#FF9800",
                              color: "#FFF",
                              fontSize: "0.7rem",
                            }}
                          />
                        ) : (
                          <Chip
                            label="FACTURADO"
                            size="small"
                            style={{
                              backgroundColor: "#4CAF50",
                              color: "#FFF",
                              fontSize: "0.7rem",
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          style={{
                            fontWeight: 600,
                            color:
                              candidato.diferencia_monto === 0
                                ? "#4CAF50"
                                : "#FF9800",
                          }}
                        >
                          {formatearMonto(candidato.monto)}
                        </Typography>
                        {candidato.diferencia_monto !== 0 && (
                          <Typography variant="caption" color="textSecondary">
                            Dif: {formatearMonto(candidato.diferencia_monto)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={candidato.score}
                          size="small"
                          style={{
                            backgroundColor: scoreConfig.badgeColor,
                            color: "#FFF",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scoreConfig.label}
                          size="small"
                          style={{
                            backgroundColor: `${scoreConfig.badgeColor}20`,
                            color: scoreConfig.badgeColor,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                    </TableRow>

                    {/* Fila expandible con razones */}
                    <TableRow key={`exp-${key}`}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={7}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box margin={2}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              gutterBottom
                            >
                              RAZONES DE COINCIDENCIA:
                            </Typography>
                            <Box mt={1} display="flex" flexWrap="wrap">
                              {candidato.razones &&
                                candidato.razones.map((razon, idx) => (
                                  <Chip
                                    key={idx}
                                    label={razon}
                                    size="small"
                                    className={classes.razonesChip}
                                    style={{
                                      backgroundColor: "#F5F5F5",
                                      border: "1px solid #E0E0E0",
                                    }}
                                  />
                                ))}
                            </Box>

                            <Box mt={2}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                gutterBottom
                              >
                                DETALLES:
                              </Typography>
                              <Box mt={1}>
                                <Typography variant="caption" display="block">
                                  <strong>Fecha emisión:</strong>{" "}
                                  {formatearFechaDisplay(
                                    candidato.fecha_emision,
                                  )}
                                </Typography>
                                {candidato.fecha_vencimiento && (
                                  <Typography variant="caption" display="block">
                                    <strong>Fecha vencimiento:</strong>{" "}
                                    {formatearFechaDisplay(
                                      candidato.fecha_vencimiento,
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Renderiza las combinaciones posibles
  const renderCombinaciones = () => {
    if (!tieneCombinaciones()) return null;

    const combinaciones = sugerencias.combinaciones_posibles;

    return (
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          COMBINACIONES POSIBLES ({combinaciones.length})
        </Typography>
        <Typography variant="caption" color="textSecondary" paragraph>
          Múltiples facturas que juntas suman el monto del movimiento
        </Typography>

        {combinaciones.map((combo, index) => {
          const isExpanded = expandedCombos.has(index);
          const scoreConfig = getScoreConfig(combo.score);

          return (
            <Card key={index} variant="outlined" style={{ marginBottom: 8 }}>
              <CardContent style={{ padding: "12px 16px" }}>
                {/* Header de la combinación */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => handleToggleCombo(index)}
                  style={{ cursor: "pointer" }}
                >
                  <Box display="flex" alignItems="center" flex={1}>
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Box ml={1}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Combinación #{index + 1} - {combo.facturas.length}{" "}
                        {combo.facturas.length === 1 ? "factura" : "facturas"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Suma total: {formatearMonto(combo.suma_total)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1}>
                    {combo.diferencia === 0 ? (
                      <Chip
                        label="Monto exacto"
                        size="small"
                        style={{
                          backgroundColor: "#E8F5E9",
                          color: "#2E7D32",
                          fontWeight: 600,
                        }}
                      />
                    ) : (
                      <Chip
                        label={`Dif: ${formatearMonto(combo.diferencia)}`}
                        size="small"
                        style={{
                          backgroundColor: "#FFF3E0",
                          color: "#F57C00",
                          fontWeight: 600,
                        }}
                      />
                    )}
                    <Chip
                      label={`Score: ${combo.score}`}
                      size="small"
                      style={{
                        backgroundColor: scoreConfig.badgeColor,
                        color: "#FFF",
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                </Box>

                {/* Contenido expandible */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box mt={2}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      FACTURAS EN ESTA COMBINACIÓN:
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Folio</TableCell>
                            <TableCell align="right">Monto</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {combo.facturas.map((factura, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Chip
                                  label={factura.tipo}
                                  size="small"
                                  style={{
                                    backgroundColor:
                                      factura.tipo === "PLAN"
                                        ? "#E3F2FD"
                                        : "#FFF3E0",
                                    color:
                                      factura.tipo === "PLAN"
                                        ? "#1976D2"
                                        : "#F57C00",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  #{factura.folio}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 600 }}
                                >
                                  {formatearMonto(factura.monto)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow>
                            <TableCell colSpan={2} align="right">
                              <Typography
                                variant="body2"
                                style={{ fontWeight: 700 }}
                              >
                                TOTAL:
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                style={{
                                  fontWeight: 700,
                                  color: "#4CAF50",
                                }}
                              >
                                {formatearMonto(combo.suma_total)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* HEADER */}
      <DialogTitle className={classes.dialogTitle}>
        Sugerencias de Conciliación
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          disabled={procesando || isConciliando}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent>
        {/* LOADING STATE */}
        {(isLoading || isFetching) && (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        )}

        {/* ERROR STATE */}
        {isError && (
          <Alert severity="error">
            Error al cargar sugerencias:{" "}
            {error?.data?.message || error?.message}
          </Alert>
        )}

        {/* SUCCESS STATE */}
        {!isLoading && !isFetching && !isError && sugerencias && (
          <Box>
            {/* Información del movimiento */}
            {renderInfoMovimiento()}

            {/* Mejor match - Solo si existe con datos */}
            {tieneMejorMatch() && renderMejorMatch()}

            {/* Candidatos individuales */}
            {tieneCandidatos() && renderCandidatosIndividuales()}

            {/* Combinaciones posibles */}
            {tieneCombinaciones() && renderCombinaciones()}

            {/* Mensaje si NO hay ninguna sugerencia */}
            {!tieneMejorMatch() &&
              !tieneCandidatos() &&
              !tieneCombinaciones() && (
                <Box className={classes.section}>
                  <Alert severity="info" icon={<WarningIcon />}>
                    <Typography variant="body2" gutterBottom>
                      <strong>
                        No se encontraron coincidencias automáticas
                      </strong>
                    </Typography>
                    <Typography variant="caption">
                      El motor de matching no encontró facturas que coincidan
                      con este movimiento. Puede realizar una conciliación
                      manual buscando facturas específicas.
                    </Typography>
                  </Alert>
                </Box>
              )}

            {/* Mensaje si hay candidatos pero sin mejor match */}
            {!tieneMejorMatch() &&
              (tieneCandidatos() || tieneCombinaciones()) && (
                <Box className={classes.section}>
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <Typography variant="body2" gutterBottom>
                      <strong>No hay coincidencias de alta confianza</strong>
                    </Typography>
                    <Typography variant="caption">
                      Se encontraron{" "}
                      {tieneCandidatos()
                        ? sugerencias.candidatos_individuales.length
                        : 0}{" "}
                      candidatos
                      {tieneCombinaciones() &&
                        ` y ${sugerencias.combinaciones_posibles.length} combinaciones`}{" "}
                      pero con scores bajos. Revise las opciones a continuación
                      o realice una conciliación manual.
                    </Typography>
                  </Alert>
                </Box>
              )}
          </Box>
        )}
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions>
        <Button onClick={handleClose} disabled={procesando || isConciliando}>
          Cancelar
        </Button>

        {/* Botón Conciliar Manual - Siempre disponible */}
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            handleClose();
            if (onConciliarManual) {
              onConciliarManual(movimiento);
            }
          }}
          disabled={procesando || isConciliando}
        >
          Conciliar Manual
        </Button>
      </DialogActions>
    </Dialog>
  );
}
